import { useState, useEffect } from 'react';
import { Save, User, Bell, Shield, Palette, Plug, CheckCircle, Eye, EyeOff, Database, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/common/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/common/Tabs';
import { Select, SelectOption } from '@/components/common/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/Toast';
import { useAuthStore } from '@/hooks/useAuthStore';
import apiClient from '@/services/api/apiClient';

const roleOptions: SelectOption[] = [
  { value: 'MDA', label: 'Marketing Development Associate' },
  { value: 'ABM_OPS', label: 'ABM Operations' },
  { value: 'ABM_MANAGER', label: 'ABM Manager' },
  { value: 'SALES', label: 'Sales' },
  { value: 'ADMIN', label: 'Administrator' },
];

const timezoneOptions: SelectOption[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
];

const themeOptions: SelectOption[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];


// ── Integrations Panel ────────────────────────────────────────────────────────
interface ConnectorField {
  key: string;
  label: string;
  placeholder?: string;
  secret?: boolean;
  hint?: string;
  type?: 'text' | 'select';
  options?: { value: string; label: string }[];
  showIf?: (v: Record<string, string>) => boolean;
}

const CONNECTORS: Array<{
  id: string;
  name: string;
  logo: string;
  description: string;
  docsUrl: string;
  testable?: boolean;
  fields: ConnectorField[];
}> = [
  {
    id: 'vapi',
    name: 'Vapi.ai',
    logo: '🔊',
    description: 'AI voice calling platform with real-time transcription, function calling, and custom voices.',
    docsUrl: 'https://docs.vapi.ai/quickstart/phone/outbound',
    fields: [
      { key: 'apiKey', label: 'API Key', placeholder: 'sk-...', secret: true, hint: 'From dashboard.vapi.ai → API Keys' },
      { key: 'phoneNumberId', label: 'Phone Number ID', placeholder: 'phone_...', hint: 'The Vapi phone number to call from' },
      { key: 'assistantId', label: 'Assistant ID (optional)', placeholder: 'asst_...', hint: 'Pre-built assistant — leave blank for dynamic agents' },
      { key: 'voiceId', label: 'Voice ID', placeholder: 'jennifer', hint: 'PlayHT or ElevenLabs voice ID (default: jennifer)' },
      { key: 'webhookSecret', label: 'Webhook Secret (optional)', placeholder: 'long-random-string', secret: true, hint: 'Set the same value as your Vapi Server URL Secret to authenticate inbound call webhooks' },
    ],
  },
  {
    id: 'retell',
    name: 'Retell AI',
    logo: '📞',
    description: 'Low-latency AI voice calling with multi-language support and advanced conversation management.',
    docsUrl: 'https://docs.retellai.com/make-calls/outbound-call',
    fields: [
      { key: 'apiKey', label: 'API Key', placeholder: 'key_...', secret: true, hint: 'From app.retellai.com → Settings → API Keys' },
      { key: 'fromNumber', label: 'From Phone Number', placeholder: '+1234567890', hint: 'Your Retell-provisioned number (E.164 format)' },
      { key: 'agentId', label: 'Agent ID (optional)', placeholder: 'agent_...', hint: 'Pre-built Retell agent — leave blank for dynamic' },
      { key: 'llmId', label: 'LLM ID (optional)', placeholder: 'llm_...', hint: 'Your Retell LLM — used to inject ABM context dynamically' },
      { key: 'voiceId', label: 'Voice ID', placeholder: '11labs-Adrian', hint: 'ElevenLabs or other voice (default: 11labs-Adrian)' },
    ],
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    logo: '🤖',
    description: 'Unified LLM gateway — powers Email Agent, Voice Agent AI features.',
    docsUrl: 'https://openrouter.ai/keys',
    fields: [
      { key: 'apiKey', label: 'API Key', placeholder: 'sk-or-v1-...', secret: true, hint: 'From openrouter.ai/keys' },
      { key: 'model', label: 'Default Model', placeholder: 'anthropic/claude-sonnet-4', hint: 'Default model for email/voice agents' },
    ],
  },
  {
    id: 'email',
    name: 'Email Gateway',
    logo: '✉️',
    description: 'Outbound email for magnet delivery and the Email Agent. Switch providers without code changes — open/click tracking is preserved across all of them.',
    docsUrl: 'https://docs.aws.amazon.com/ses/latest/dg/send-email.html',
    testable: true,
    fields: [
      { key: 'provider', label: 'Provider', type: 'select', hint: 'Which service actually sends the email',
        options: [
          { value: 'ses', label: 'Amazon SES' },
          { value: 'smtp', label: 'SMTP (any server)' },
          { value: 'sendgrid', label: 'SendGrid' },
          { value: 'resend', label: 'Resend' },
        ] },
      { key: 'from', label: 'From address', placeholder: 'Cantier <outreach@cantier.biz>', hint: 'Verified sender. Format: Name <email@domain>' },
      { key: 'replyTo', label: 'Reply-To (optional)', placeholder: 'sales@cantier.biz' },
      // SES
      { key: 'region', label: 'AWS Region', placeholder: 'ap-south-1', hint: 'Region where your SES domain is verified', showIf: v => (v.provider || 'ses') === 'ses' },
      // SMTP
      { key: 'smtpHost', label: 'SMTP Host', placeholder: 'smtp.example.com', showIf: v => v.provider === 'smtp' },
      { key: 'smtpPort', label: 'SMTP Port', placeholder: '587', hint: '587 = STARTTLS, 465 = SSL', showIf: v => v.provider === 'smtp' },
      { key: 'smtpSecure', label: 'Connection security', type: 'select',
        options: [{ value: 'false', label: 'STARTTLS (port 587/25)' }, { value: 'true', label: 'SSL/TLS (port 465)' }],
        showIf: v => v.provider === 'smtp' },
      { key: 'smtpUser', label: 'SMTP Username', placeholder: 'apikey or user@domain', showIf: v => v.provider === 'smtp' },
      { key: 'smtpPassword', label: 'SMTP Password', placeholder: '••••••••', secret: true, showIf: v => v.provider === 'smtp' },
      // SendGrid / Resend
      { key: 'apiKey', label: 'API Key', placeholder: 'SG.… (SendGrid) or re_… (Resend)', secret: true, hint: 'API key for the selected HTTP provider', showIf: v => v.provider === 'sendgrid' || v.provider === 'resend' },
    ],
  },
];

function ConnectorCard({ connector, addToast }: { connector: typeof CONNECTORS[0]; addToast: any }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [show, setShow] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setTesting(true);
    try {
      const r = await apiClient.post(`/api/connectors/${connector.id}/test`, { to: testTo.trim() });
      addToast({ type: 'success', title: 'Test email sent', description: `Sent to ${r.data?.sentTo} via ${r.data?.provider}. Check the inbox.` });
    } catch (e: any) {
      addToast({ type: 'error', title: 'Test failed', description: e.response?.data?.message || e.response?.data?.error || e.message });
    } finally { setTesting(false); }
  };

  useEffect(() => {
    apiClient.get(`/api/connectors/${connector.id}`).then(r => {
      const cfg = r.data?.config || {};
      setValues(cfg);
      setConfigured(Object.values(cfg).some(v => v && (v as string).length > 3));
    }).catch(() => {});
  }, [connector.id]);

  const save = async () => {
    setLoading(true);
    try {
      await apiClient.put(`/api/connectors/${connector.id}`, { config: values });
      setSaved(true);
      setConfigured(true);
      addToast({ type: 'success', title: `${connector.name} saved`, description: 'Configuration updated successfully.' });
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Save failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className={`rounded-xl border p-5 space-y-4 ${configured ? 'border-emerald-200 dark:border-emerald-800' : 'border-border'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{connector.logo}</span>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold">{connector.name}</p>
              {configured && <CheckCircle className="h-4 w-4 text-emerald-500" />}
            </div>
            <p className="text-xs text-muted-foreground">{connector.description}</p>
          </div>
        </div>
        <a href={connector.docsUrl} target="_blank" rel="noopener noreferrer"
          className="text-xs text-primary hover:underline shrink-0">Docs →</a>
      </div>

      <div className="space-y-3">
        {connector.fields.filter(f => !f.showIf || f.showIf(values)).map(f => (
          <div key={f.key}>
            <label className="text-xs font-medium text-muted-foreground block mb-1">{f.label}</label>
            {f.type === 'select' ? (
              <select
                value={values[f.key] ?? (f.options?.[0]?.value || '')}
                onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                className="w-full text-sm px-3 py-1.5 rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <div className="relative">
                <input
                  type={f.secret && !show[f.key] ? 'password' : 'text'}
                  value={values[f.key] || ''}
                  onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full text-sm px-3 py-1.5 rounded-md border border-border bg-background pr-8 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {f.secret && (
                  <button onClick={() => setShow(s => ({ ...s, [f.key]: !s[f.key] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {show[f.key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            )}
            {f.hint && <p className="text-[10px] text-muted-foreground mt-0.5">{f.hint}</p>}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={save} disabled={loading}
          className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {loading ? <span className="animate-spin">⟳</span> : saved ? <CheckCircle className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
          {saved ? 'Saved!' : loading ? 'Saving…' : 'Save Configuration'}
        </button>
      </div>

      {connector.testable && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <input
            type="email"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 text-sm px-3 py-1.5 rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button onClick={sendTest} disabled={testing || !testTo.trim()}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:border-primary/40 disabled:opacity-50 transition-colors shrink-0">
            {testing ? <span className="animate-spin">⟳</span> : '✉️'} Send test
          </button>
        </div>
      )}
    </div>
  );
}

function IntegrationsPanel({ addToast }: { addToast: any }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Integrations</h3>
        <p className="text-sm text-muted-foreground">Configure external services — AI calling providers, LLM gateways, and enrichment tools.</p>
      </div>
      <div className="space-y-4">
        {CONNECTORS.map(c => (
          <ConnectorCard key={c.id} connector={c} addToast={addToast} />
        ))}
      </div>
    </div>
  );
}

// ── Automation Panel ──────────────────────────────────────────────────────────
function AutomationPanel({ addToast, canEdit }: { addToast: any; canEdit: boolean }) {
  const [mode, setMode] = useState<'auto' | 'validate'>('validate');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get('/api/connectors/automation')
      .then(r => {
        const m = r.data?.config?.journeyUpdateMode;
        if (m === 'auto' || m === 'validate') setMode(m);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/connectors/automation', { config: { journeyUpdateMode: mode } });
      addToast({
        type: 'success',
        title: 'Automation updated',
        description: mode === 'auto'
          ? 'AI updates now apply to journeys automatically.'
          : 'AI updates now wait for MDA validation.',
      });
    } catch (e: any) {
      addToast({ type: 'error', title: 'Save failed', description: e.response?.data?.error || e.message });
    } finally { setSaving(false); }
  };

  const options = [
    { value: 'validate' as const, title: 'MDA validation', isDefault: true,
      desc: 'AI-generated stage advances and field updates are staged as proposals. An MDA reviews and approves before the journey changes. Safer — recommended.' },
    { value: 'auto' as const, title: 'Auto-apply', isDefault: false,
      desc: 'AI applies stage advances and field updates to the journey automatically after each call. Faster, but trusts model output without review.' },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Journey automation</h3>
        <p className="text-sm text-muted-foreground">
          Controls what happens when an AI agent (Voice Agent, AI Calling) generates an update to an ABM journey —
          apply it automatically, or hold it for MDA validation.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-3">
          {options.map(o => {
            const selected = mode === o.value;
            return (
              <button
                key={o.value}
                type="button"
                disabled={!canEdit}
                onClick={() => setMode(o.value)}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'} ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${selected ? 'border-primary' : 'border-muted-foreground'}`}>
                    {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </span>
                  <p className="text-sm font-bold">{o.title}</p>
                  {o.isDefault && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">default</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 ml-6">{o.desc}</p>
              </button>
            );
          })}

          {!canEdit && (
            <p className="text-xs text-muted-foreground">Only Admins and Managers can change this setting.</p>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving || !canEdit}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save Automation Setting'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Backup Panel (Admin only) ─────────────────────────────────────────────────
function BackupPanel({ addToast }: { addToast: any }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get('/api/admin/db-backup', {
        responseType: 'blob',
        timeout: 0, // a full dump can take a while — don't time out
      });

      // Filename from Content-Disposition, else a sensible default.
      const cd: string = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const filename = match?.[1] || `cantier_abm_backup_${stamp}.sql.gz`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      addToast({ type: 'success', title: 'Backup downloaded', description: filename });
    } catch (e: any) {
      // Error body comes back as a Blob (responseType=blob) — read it for the message.
      let msg = e.response?.data?.error || e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch { /* keep msg */ }
      }
      addToast({ type: 'error', title: 'Backup failed', description: msg });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Database backup</h3>
        <p className="text-sm text-muted-foreground">
          Download a complete, point-in-time snapshot of the portal database as a compressed
          SQL file (<code className="text-xs">.sql.gz</code>). Use it for off-site backups or to
          restore the database elsewhere.
        </p>
      </div>

      <div className="rounded-xl border border-border p-5 space-y-4">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-primary/10 p-2 text-primary"><Database className="h-5 w-5" /></span>
          <div className="space-y-1">
            <p className="text-sm font-bold">Full database snapshot</p>
            <p className="text-xs text-muted-foreground">
              Includes every table — accounts, contacts, journeys, prospects, users, and logs.
              The file contains sensitive data; store it securely.
            </p>
          </div>
        </div>

        <Button onClick={download} disabled={downloading}>
          {downloading
            ? <><span className="animate-spin mr-2">⟳</span>Preparing backup…</>
            : <><Download className="h-4 w-4 mr-2" />Download Database Backup</>}
        </Button>

        <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
          Restore with: <code className="text-xs">gunzip -c &lt;file&gt;.sql.gz | docker exec -i cantier-db psql -U postgres cantier_abm_portal</code>
        </p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { addToast } = useToast();
  const { user } = useAuthStore();
  const isAdmin = ['ADMIN', 'ADMINISTRATOR'].includes(user?.role || '');
  const canEditAutomation = isAdmin || user?.role === 'ABM_MANAGER';

  // Profile settings
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState(user?.role || 'MDA');
  const [timezone, setTimezone] = useState('America/New_York');

  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [weeklyReport, setWeeklyReport] = useState(true);

  // Appearance settings
  const [theme, setTheme] = useState('light');
  const [compactMode, setCompactMode] = useState(false);

  const handleSaveProfile = () => {
    addToast({
      type: 'success',
      title: 'Profile Updated',
      description: 'Your profile has been updated successfully.',
    });
  };

  const handleSaveNotifications = () => {
    addToast({
      type: 'success',
      title: 'Notifications Updated',
      description: 'Your notification preferences have been saved.',
    });
  };

  const handleSaveAppearance = () => {
    addToast({
      type: 'success',
      title: 'Appearance Updated',
      description: 'Your appearance settings have been saved.',
    });
  };

  const handleChangePassword = () => {
    addToast({
      type: 'info',
      title: 'Coming Soon',
      description: 'Password change feature coming soon!',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Settings"
        description="Manage your account settings and preferences"
      />

      {/* Settings Tabs */}
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="integrations">
              <Plug className="h-4 w-4 mr-2" />
              Integrations
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="backup">
              <Database className="h-4 w-4 mr-2" />
              Backup
            </TabsTrigger>
          )}
          {canEditAutomation && (
            <TabsTrigger value="automation">
              <CheckCircle className="h-4 w-4 mr-2" />
              Automation
            </TabsTrigger>
          )}
          <TabsTrigger value="appearance">
            <Palette className="h-4 w-4 mr-2" />
            Appearance
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal information and role
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Role</label>
                  <Select
                    options={roleOptions}
                    value={role}
                    onChange={setRole}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Timezone</label>
                  <Select
                    options={timezoneOptions}
                    value={timezone}
                    onChange={setTimezone}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveProfile}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Choose how you want to be notified
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Receive email updates about your account activity
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium">Push Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Receive push notifications in your browser
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={pushNotifications}
                  onChange={(e) => setPushNotifications(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium">Weekly Report</p>
                  <p className="text-sm text-muted-foreground">
                    Get a weekly summary of your activity
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={weeklyReport}
                  onChange={(e) => setWeeklyReport(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveNotifications}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Manage your password and security preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Password</label>
                <Input type="password" placeholder="Enter current password" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">New Password</label>
                <Input type="password" placeholder="Enter new password" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm New Password</label>
                <Input type="password" placeholder="Confirm new password" />
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleChangePassword}>
                  <Shield className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </div>

              <div className="border-t pt-4 mt-6">
                <h3 className="font-medium mb-4">Active Sessions</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">Current Session</p>
                      <p className="text-sm text-muted-foreground">
                        Chrome on Windows • Bengaluru, India
                      </p>
                    </div>
                    <span className="text-xs text-green-600 font-medium">Active</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>Appearance Settings</CardTitle>
              <CardDescription>
                Customize how the application looks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Theme</label>
                <Select
                  options={themeOptions}
                  value={theme}
                  onChange={setTheme}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium">Compact Mode</p>
                  <p className="text-sm text-muted-foreground">
                    Reduce spacing for a more condensed layout
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={compactMode}
                  onChange={(e) => setCompactMode(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveAppearance}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Appearance
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab — Admin only */}
        {isAdmin && (
          <TabsContent value="integrations">
            <IntegrationsPanel addToast={addToast} />
          </TabsContent>
        )}

        {/* Backup Tab — Admin only */}
        {isAdmin && (
          <TabsContent value="backup">
            <Card>
              <CardHeader>
                <CardTitle>Backup</CardTitle>
                <CardDescription>
                  Download a full snapshot of the portal database
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BackupPanel addToast={addToast} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Automation Tab — Admin + Manager only */}
        {canEditAutomation && (
        <TabsContent value="automation">
          <Card>
            <CardHeader>
              <CardTitle>Automation</CardTitle>
              <CardDescription>
                Decide whether AI agents update ABM journeys automatically or wait for human validation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AutomationPanel addToast={addToast} canEdit={canEditAutomation} />
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
